import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Switch, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CookieIcon from '@mui/icons-material/Cookie';

const CONSENT_KEY = 'cookie_consent';
const CONSENT_EXPIRY_KEY = 'cookie_consent_expiry';
const CONSENT_DURATION_MS = 365 * 24 * 60 * 60 * 1000; // 12 miesięcy

const defaultPreferences = {
  necessary: true,    // zawsze włączone
  analytics: false,
  marketing: false,
};

function getStoredConsent() {
  try {
    const expiry = localStorage.getItem(CONSENT_EXPIRY_KEY);
    if (expiry && Date.now() > Number(expiry)) {
      localStorage.removeItem(CONSENT_KEY);
      localStorage.removeItem(CONSENT_EXPIRY_KEY);
      return null;
    }
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveConsent(preferences) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences));
  localStorage.setItem(CONSENT_EXPIRY_KEY, String(Date.now() + CONSENT_DURATION_MS));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true };
    saveConsent(all);
    setVisible(false);
  };

  const handleRejectAll = () => {
    const minimal = { necessary: true, analytics: false, marketing: false };
    saveConsent(minimal);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent({ ...preferences, necessary: true });
    setVisible(false);
    setShowSettings(false);
  };

  // Ikona do ponownego otwarcia ustawień (tylko gdy brak zapisanej zgody)
  const hasConsent = !!getStoredConsent();
  const reopenButton = !visible && !hasConsent ? (
    <Box
      onClick={() => {
        setShowSettings(true);
        setVisible(true);
      }}
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        zIndex: 9998,
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: '#D2691E',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'scale(1.1)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        },
      }}
      title="Ustawienia cookies"
    >
      <CookieIcon sx={{ color: 'white', fontSize: 22 }} />
    </Box>
  ) : null;

  if (!visible) return reopenButton;

  return (
    <>
      {/* Overlay */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.35)',
          zIndex: 9998,
        }}
      />

      {/* Banner */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          borderTop: '3px solid #D2691E',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
          px: { xs: 2, sm: 4 },
          py: 3,
        }}
      >
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          {/* Nagłówek */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CookieIcon sx={{ color: '#D2691E', fontSize: 26 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#333' }}>
                Ta strona używa plików cookies
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleRejectAll}
              sx={{ color: '#999', mt: -0.5 }}
              aria-label="Zamknij"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Opis */}
          <Typography variant="body2" sx={{ color: '#555', mb: 2, lineHeight: 1.6 }}>
            Używamy plików cookies i podobnych technologii, aby zapewnić prawidłowe działanie serwisu 
            oraz — za Twoją zgodą — do celów analitycznych i marketingowych. 
            Możesz zaakceptować wszystkie cookies, odrzucić opcjonalne lub dostosować swoje preferencje. 
            Więcej informacji znajdziesz w naszej{' '}
            <a
              href="/polityka-prywatnosci"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#D2691E', textDecoration: 'underline' }}
            >
              Polityce Prywatności
            </a>.
          </Typography>

          {/* Panel ustawień granularnych */}
          {showSettings && (
            <Box sx={{
              backgroundColor: '#FAFAFA',
              border: '1px solid #E5E7EB',
              borderRadius: 2,
              p: 2,
              mb: 2,
            }}>
              {/* Niezbędne */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#333' }}>
                    Niezbędne
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Wymagane do działania strony (sesja, logowanie). Zawsze aktywne.
                  </Typography>
                </Box>
                <Switch checked disabled size="small" sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#D2691E' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D2691E' },
                }} />
              </Box>

              {/* Analityczne */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#333' }}>
                    Analityczne
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Pomagają nam zrozumieć, jak korzystasz z serwisu (np. Google Analytics, HotJar).
                  </Typography>
                </Box>
                <Switch
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#D2691E' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D2691E' },
                  }}
                />
              </Box>

              {/* Marketingowe */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#333' }}>
                    Marketingowe
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Służą do personalizacji reklam (np. Facebook Pixel, Google Ads, TikTok Pixel).
                  </Typography>
                </Box>
                <Switch
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#D2691E' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D2691E' },
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Przyciski akcji */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            justifyContent: 'flex-end',
            alignItems: { xs: 'stretch', sm: 'center' },
          }}>
            {!showSettings ? (
              <Button
                variant="text"
                onClick={() => setShowSettings(true)}
                sx={{
                  textTransform: 'none',
                  color: '#D2691E',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  order: { xs: 3, sm: 1 },
                  '&:hover': { backgroundColor: 'rgba(210,105,30,0.06)' },
                }}
              >
                Zarządzaj ustawieniami
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSavePreferences}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  backgroundColor: '#555',
                  order: { xs: 3, sm: 1 },
                  '&:hover': { backgroundColor: '#333' },
                }}
              >
                Zapisz ustawienia
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleRejectAll}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderColor: '#D1D5DB',
                color: '#555',
                order: { xs: 2, sm: 2 },
                '&:hover': { borderColor: '#999', backgroundColor: '#F9FAFB' },
              }}
            >
              Odrzuć opcjonalne
            </Button>
            <Button
              variant="contained"
              onClick={handleAcceptAll}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
                backgroundColor: '#D2691E',
                px: 3,
                order: { xs: 1, sm: 3 },
                '&:hover': { backgroundColor: '#B85C00' },
              }}
            >
              Akceptuj wszystkie
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// Helper do sprawdzania zgody w innych komponentach
export function getCookieConsent() {
  return getStoredConsent() || defaultPreferences;
}
